import { anonymousHttpClient, httpClient } from "../../../lib/http/axios";
import type { ApiEnvelope } from "../../../lib/http/contracts";
import type { GroupInfo, GroupLink, ImagePair, MemberOption, RecognitionResult, RefundFilters, RefundPage, ReviewRequest, SavedResult, SubmitRequest } from "../types";
import type * as Contract from "../api/adapters";
import type { RefundRosterPage } from "../types";

const teamPath = (teamId: string) => `/ticket-refunds/teams/${encodeURIComponent(teamId)}`;
const publicPath = (token: string) => `/ticket-refunds/public/${encodeURIComponent(token)}`;

/** 仅在员工打开抽屉后读取本团名单。 */
export async function fetchRefundRoster(teamId: string, page: number, pageSize: number): Promise<RefundRosterPage> {
  const params = { page, page_size: pageSize } satisfies Contract.RefundRosterQuery;
  return unwrap((await httpClient.get<ApiEnvelope<Contract.RefundRosterPage>>(`${teamPath(teamId)}/roster`, { params })).data);
}

/** 空响应不能被当作成功提交。 */
function unwrap<T>(body: ApiEnvelope<T>): T {
  if (body.code !== "OK" || body.data == null) throw new Error("退票服务返回异常，请重试");
  return body.data;
}

/** 读取员工可管理团的二维码资料。 */
export async function fetchRefundGroup(teamId: string): Promise<GroupLink> {
  return unwrap((await httpClient.get<ApiEnvelope<Contract.GroupLink>>(teamPath(teamId))).data);
}

/** 分页读取审核记录。 */
export async function fetchRefundRequests(teamId: string, page: number, pageSize: number, filters: RefundFilters = {}): Promise<RefundPage> {
  const params = { page, page_size: pageSize, ...filters } satisfies Contract.RefundListQuery;
  return unwrap((await httpClient.get<ApiEnvelope<Contract.RefundPage>>(`${teamPath(teamId)}/requests`, { params })).data);
}

/** 保存单次人工审核。 */
export async function reviewRefund(teamId: string, id: string, body: ReviewRequest): Promise<SavedResult> {
  return unwrap((await httpClient.post<ApiEnvelope<Contract.SavedResult>>(`${teamPath(teamId)}/requests/${encodeURIComponent(id)}/review`, body satisfies Contract.ReviewRequest)).data);
}

/** 确认后彻底删除申请和凭证，失败由用户决定重试。 */
export async function deleteRefund(teamId: string, id: string): Promise<SavedResult> {
  return unwrap((await httpClient.delete<ApiEnvelope<Contract.SavedResult>>(`${teamPath(teamId)}/requests/${encodeURIComponent(id)}`, { "axios-retry": { retries: 0 } })).data);
}

/** 凭证必须携带员工身份读取，不能公开图片地址。 */
export async function fetchRefundImage(teamId: string, id: string, kind: "ticket" | "refund") {
  return (await httpClient.get<Blob>(`${teamPath(teamId)}/requests/${encodeURIComponent(id)}/images/${kind}`, { responseType: "blob" })).data;
}

/** 公开表单只读取签名链接对应的团。 */
export async function fetchPublicRefundGroup(token: string): Promise<GroupInfo> {
  return unwrap((await anonymousHttpClient.get<ApiEnvelope<Contract.GroupInfo>>(publicPath(token))).data);
}

/** 按固定顺序提交两张图片供模型识别。 */
export async function recognizeRefund(token: string, images: ImagePair): Promise<RecognitionResult> {
  return unwrap((await anonymousHttpClient.post<ApiEnvelope<Contract.RecognitionResult>>(`${publicPath(token)}/recognize`, images satisfies Contract.ImagePair, { timeout: 90_000 })).data);
}

/** 输入姓名只匹配识别到的本人及其所在团。 */
export async function findRefundMembers(token: string, receipt: string, name: string): Promise<MemberOption[]> {
  return unwrap((await anonymousHttpClient.post<ApiEnvelope<Contract.MemberOption[]>>(`${publicPath(token)}/members`, { receipt, name })).data);
}

/** 提交申请，不自动重试非幂等写入。 */
export async function submitRefund(token: string, body: SubmitRequest): Promise<SavedResult> {
  return unwrap((await anonymousHttpClient.post<ApiEnvelope<Contract.SavedResult>>(`${publicPath(token)}/submit`, body satisfies Contract.SubmitRequest)).data);
}
